import React, { useState, useRef, useCallback } from 'react';
import { FiArrowLeft, FiFile, FiCheckCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Tooltip from '../../../components/tooltip';
import { submitCompliance, ComplianceType, saveComplianceResult, FileBufferInfo, generateConversationId } from '../../../services/compliance';
import Loading from '../../../components/Loading';
import {
  Container,
  Header,
  BackButton,
  TitleContainer,
  Title,
  Subtitle,
  DropzoneContainer,
  DropzoneArea,
  DropzoneText,
  OrText,
  UploadButton,
  HiddenInput,
  FileInfo,
  FileName,
  FileIcon,
  SuccessMessage
} from './styles';

// Interface para o estado de navegação
interface NavigationState {
  showModal: boolean;
  modalData: any;
  completed: boolean;
  resultContent: string;
  apiResponse: any;
  fields: any;
  dataHasBeenSaved: boolean;
  isNewSubmission?: boolean;
}

export const FileUpload: React.FC = () => {
  const navigate = useNavigate();
  const [isDragActive, setIsDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Função auxiliar para converter buffer de arquivo em texto
  const fileBufferToText = async (buffer: ArrayBuffer): Promise<string> => {
    try {
      // Primeiro tenta ler como texto
      const blob = new Blob([buffer]);
      return await blob.text();
    } catch (error) {
      console.error('Erro ao converter buffer para texto:', error);
      return 'Não foi possível extrair o conteúdo do arquivo.';
    }
  };

  const handleBack = () => {
    navigate('/compliance');
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const processFile = async (selectedFile: File) => {
    setFile(selectedFile);
    setIsUploaded(false);
    setIsLoading(true);
    
    try {
      // Converter o arquivo em buffer (ArrayBuffer)
      const fileBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(new Error('Erro ao ler o arquivo'));
        reader.readAsArrayBuffer(selectedFile);
      });
      
      // Criar objeto com informações do buffer e nome do arquivo
      const fileInfo: FileBufferInfo = {
        buffer: fileBuffer,
        fileName: selectedFile.name,
        fileType: selectedFile.type
      };
      
      // Enviar o arquivo como buffer com informações adicionais
      const response = await submitCompliance(
        ComplianceType.UPLOAD_DOC,
        fileInfo
      );
      
      console.log("Resposta da API (file-upload):", response);
      
      setIsUploaded(true);
      
      // Extrair texto da resposta ou usar o nome do arquivo como fallback
      let resultText = '';
      if (response.content && response.content[0]?.text) {
        resultText = response.content[0].text;
      } else if (response.content && response.content[0]?.complianceForm) {
        resultText = JSON.stringify(response.content[0].complianceForm, null, 2);
      } else {
        resultText = `Conteúdo do arquivo: ${selectedFile.name}`;
      }

      // Preparar state para navegação
      const navigationState = {
        showModal: true,
        modalData: { 
          name: "User",
          birthDate: "",
          fileName: selectedFile.name
        },
        completed: true,
        resultContent: resultText,
        apiResponse: response,
        fields: response.content && response.content[0]?.complianceForm?.complianceFormSchema
          ? response.content[0].complianceForm.complianceFormSchema
          : null,
        dataHasBeenSaved: true,
        isNewSubmission: true
      };

      // Sempre navegar para compliance/pending independente do resultado
      setIsLoading(false);
      navigate('/compliance/pending?full=true', { 
        state: navigationState,
        replace: true
      });
    } catch (error) {
      console.error('Erro ao processar o arquivo:', error);
      // Mostrar a mensagem específica do erro, se disponível
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Ocorreu um erro ao processar o arquivo. Tente novamente.';
      alert(errorMessage);
      setFile(null);
      setIsLoading(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      processFile(droppedFile);
    }
  }, []);
  
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  return (
    <Container>
      <Header>
        <Tooltip text="Back to Compliance">
          <BackButton onClick={handleBack}>
            {FiArrowLeft({ className: "icon", size: 30 })}
          </BackButton>
        </Tooltip>
      </Header>

      <TitleContainer>
        <Title>File Upload</Title>
        <Subtitle>Upload a document for compliance verification.</Subtitle>

        <DropzoneContainer>
          {!file ? (
            <DropzoneArea 
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={isDragActive ? 'active' : ''}
              onClick={handleClick}
            >
              <DropzoneText>
                Drag and drop your file here
              </DropzoneText>
              <OrText>or</OrText>
              <UploadButton type="button">
                Browse files
              </UploadButton>
              <small>Accepted formats: PDF, DOCX, TXT, RTF, ODT, JPG, JPEG, PNG</small>
            </DropzoneArea>
          ) : (
            <DropzoneArea className="file-loaded">
              {isUploaded ? (
                <>
                  <FileIcon>{FiCheckCircle({ size: 48 })}</FileIcon>
                  <SuccessMessage>File uploaded successfully!</SuccessMessage>
                  <FileInfo>
                    <FileName>{file.name}</FileName>
                    <span>({(file.size / 1024).toFixed(2)} KB)</span>
                  </FileInfo>
                  <UploadButton type="button" onClick={() => {
                    setFile(null);
                    setIsUploaded(false);
                  }}>
                    Upload another file
                  </UploadButton>
                </>
              ) : (
                <>
                  <FileIcon>{FiFile({ size: 48 })}</FileIcon>
                  <DropzoneText>Processing file...</DropzoneText>
                  <FileInfo>
                    <FileName>{file.name}</FileName>
                  </FileInfo>
                </>
              )}
            </DropzoneArea>
          )}
          
          <HiddenInput 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileInputChange}
            accept=".pdf,.docx,.txt,.rtf,.odt,.jpg,.jpeg,.png"
          />
        </DropzoneContainer>
      </TitleContainer>
      
      {isLoading && <Loading />}
    </Container>
  );
};

export default FileUpload; 